import Vue from 'vue';
import { useHttp } from '@zk/framework-vue';
import { Message } from 'zk-ui';
import { debounce } from 'lodash';
import store from '@/store';
import localeMap from 'common-module/src/lang/localeMap';
import config from '../config';
import axios from 'axios';
import { getLanguage } from '../lang';
import { getToken, isSamlLogoutInProgress, markSamlLogoutInProgress } from 'common-module/src/util/auth';

const CancelToken = axios.CancelToken;
function showError(message) {
  message && Message({
    message,
    type: 'error',
    duration: 2000
  });
}
export const debounceShowError = debounce(showError, 2500, { leading: true });
const abortMap = {};
const reloadKey = 'ligaai.reload';
let clearReloadTimer;
let clearedReload = false;
const getReloadUrl = () => window.sessionStorage.getItem(reloadKey);
const isSamlLogoutUrl = (url) => {
  try {
    return new URL(url, window.location.origin).pathname.startsWith('/api/saml/');
  } catch (_) {
    return false;
  }
};
export const clearAndSamlLogout = async (logoutUrl) => {
  if (isSamlLogoutInProgress() || !isSamlLogoutUrl(logoutUrl)) {
    return;
  }
  markSamlLogoutInProgress();
  try {
    await store.dispatch('user/clear');
    if (store.hasModule('shareUser')) {
      await store.dispatch('shareUser/clear');
    }
  } finally {
    window.location.replace(logoutUrl);
  }
};
export const clearAndReload = async (url) => {
  if (isSamlLogoutInProgress()) {
    return;
  }
  await store.dispatch('user/clear');
  if (store.hasModule('shareUser')) {
    await store.dispatch('shareUser/clear');
  }
  const reloadUrl = getReloadUrl();
  if (reloadUrl !== url) {
    if (clearReloadTimer) {
      clearTimeout(clearReloadTimer);
      clearReloadTimer = null;
    }
    window.sessionStorage.setItem(reloadKey, url);
    clearedReload = false;
    window.location.reload();
  }
};
export default useHttp(Vue, {
  showLog: process.env.NODE_ENV === 'development',
  assertResult: async (response, data) => {
    const { url } = response.config;
    
    // Check if response is a file download
    const contentDisposition = response.headers?.['content-disposition'];
    if (contentDisposition?.toLowerCase().includes('attachment')) {
      return response.status >= 200 && response.status < 300;
    }

    // SAML空闲超时：先清理本地状态，再使用浏览器顶层导航完成IdP登出。
    const samlLogoutPath = data.result?.samlLogoutPath;
    if (data.code === '403' && samlLogoutPath) {
      await clearAndSamlLogout(samlLogoutPath);
      window.console.warn('SAML会话超时，开始登出', url);
      return false;
    }

    // token失效
    if (data.code === '403') {
      await clearAndReload(url);
      window.console.warn('验证token失败', url);
      return false;
    }
    // 被移除
    if (data.code === '04206') {
      const pathname = window.location.pathname;
      if (['/sso/login', '/sso/register'].some(k => pathname.startsWith(k))) {
        return;
      }
      await store.dispatch('user/clear');
      window.location.reload();
    }
    if (!clearedReload) {
      const reloadUrl = getReloadUrl();
      if (!reloadUrl) {
        clearedReload = true;
      }
      if (reloadUrl === url) {
        clearReloadTimer = setTimeout(() => window.sessionStorage.removeItem(reloadKey), 5000);
        clearedReload = true;
      }
    }
    return data.code === '200';
  },
  error: (response) => response.data?.code !== '403' && debounceShowError(response.data?.subMsg || response.data?.msg || response.statusText)
}).setDefaults({
  baseURL: config.VUE_APP_BASE_API || '/',
  headers: {
    timezone: 8,
    timezoneStr: Intl.DateTimeFormat().resolvedOptions().timeZone,
    productLine: config.VUE_APP_PRODUCT_LINE || ''
  }
}).addBeforeHook((config) => {
  const token = config.headers.accesstoken || getToken() || store.state.user?.token;

  // abort start
  const abortId = `${config.url}-${config.positionId}`;
  if (config.abort) {
    if (!config.positionId) {
      console.error('必须填写positionId信息才可以使用abort');
    } else {
      if (abortMap[abortId]) {
        abortMap[abortId]?.();
      }
      config.cancelToken = new CancelToken(function executor(cancel) {
        abortMap[abortId] = cancel;
      });
    }
  }

  if (token) {
    config.headers.accesstoken = token;
  } else {
    Reflect.deleteProperty(config.headers, 'accesstoken');
  }
  config.headers['Accept-Language'] = (localeMap[getLanguage() || Vue.i18n?.locale || 'zh']).replace('_', '-');
}).addAfterHook((data) => data !== null && typeof data === 'object' && 'result' in data ? data.result : data)
  .addErrorHook(err => {
    if (axios.isCancel(err)) {
      console.log('----isCancel-err-----', err);
      return;
    }
    if (err.code === 'ECONNABORTED') {
      // 海外版
                       
      // eslint-disable-next-line
               debounceShowError('Request timed out. Your connection appears unstable — please check your network or try again later.');
              
                                                           
               
      return;
    }
    const config = err.config || err.response?.config;
    // showError false则不在页面顶部提示错误信息
    if (config && config.showError === false) {
      return;
    }
    if (err.response) {
      if (err.response.status >= 500) {
        debounceShowError(Vue.i18n?.t('message.http500') || '500');
        return;
      }
      if (err.response.status === 429) {
        debounceShowError(Vue.i18n?.t('message.http429') || '429');
        return;
      }
      if (err.response.status >= 400) {
        debounceShowError(Vue.i18n?.t('message.http400') || '400');
        return;
      }
      if (err.response.data?.code === '403') {
        return;
      }
    }
    debounceShowError(err.message);
  });
